import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useProcessSteps } from '../hooks/useAPI';

const ProcessSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: processSteps, isLoading, error } = useProcessSteps();

  // Default process steps if API fails
  const defaultSteps = [
    {
      id: 1,
      stepNumber: 1,
      title: 'Discovery & Analysis',
      description: 'We begin by understanding your business goals, challenges, and requirements through comprehensive analysis.',
      icon: '🔍'
    },
    {
      id: 2,
      stepNumber: 2,
      title: 'Strategy & Planning',
      description: 'Our team develops a detailed roadmap and technical strategy tailored to your specific needs.',
      icon: '📋'
    },
    {
      id: 3,
      stepNumber: 3,
      title: 'Design & Prototyping',
      description: 'We create user-centric designs and interactive prototypes to visualize the solution.',
      icon: '🎨'
    },
    {
      id: 4,
      stepNumber: 4,
      title: 'Development & Implementation',
      description: 'Our expert developers build robust, scalable solutions using cutting-edge technologies.',
      icon: '⚙️'
    },
    {
      id: 5,
      stepNumber: 5,
      title: 'Testing & Quality Assurance',
      description: 'Rigorous testing ensures your solution meets the highest standards of quality and performance.',
      icon: '✅'
    },
    {
      id: 6,
      stepNumber: 6,
      title: 'Deployment & Support',
      description: 'We deploy your solution and provide ongoing support to ensure continuous success.',
      icon: '🚀'
    }
  ];

  const displaySteps = processSteps && processSteps.length > 0 
    ? processSteps.sort((a, b) => a.stepNumber - b.stepNumber)
    : defaultSteps;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const lineVariants = {
    hidden: { scaleY: 0 },
    visible: {
      scaleY: 1,
      transition: { duration: 0.8, ease: 'easeOut', delay: 0.3 }
    }
  };

  return (
    <section id="process" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR PROCESS
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            How We Bring Ideas to Life
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Our proven methodology ensures successful project delivery from conception to deployment and beyond.
          </motion.p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="relative"
          >
            {/* Timeline Line */}
            <motion.div
              variants={lineVariants}
              className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-lion-yellow transform md:-translate-x-1/2 origin-top"
            />

            <div className="space-y-12 md:space-y-16">
              {displaySteps.map((step, index) => (
                <motion.div
                  key={step.id || index}
                  variants={stepVariants}
                  custom={index}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div className={`absolute left-8 md:left-1/2 w-4 h-4 bg-lion-yellow rounded-full transform ${
                    index % 2 === 0 ? 'md:-translate-x-1/2' : 'md:-translate-x-1/2'
                  } z-10`}>
                    <div className="absolute inset-0 bg-lion-yellow rounded-full animate-ping opacity-20"></div>
                  </div>

                  {/* Step Number (Mobile) */}
                  <div className="flex-shrink-0 w-16 h-16 bg-lion-yellow rounded-full flex items-center justify-center text-lion-blue font-bold text-lg md:hidden mr-6">
                    {step.stepNumber}
                  </div>

                  {/* Content Card */}
                  <div className={`flex-1 ${
                    index % 2 === 0 
                      ? 'md:pr-8 md:text-right md:ml-16' 
                      : 'md:pl-8 md:text-left md:mr-16'
                  } ml-16 md:ml-0`}>
                    <div className="bg-lion-gray p-6 md:p-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group">
                      {/* Step Number (Desktop) */}
                      <div className={`hidden md:flex items-center mb-4 ${
                        index % 2 === 0 ? 'justify-end' : 'justify-start'
                      }`}>
                        <div className="w-12 h-12 bg-lion-yellow rounded-full flex items-center justify-center text-lion-blue font-bold text-lg">
                          {step.stepNumber}
                        </div>
                      </div>

                      {/* Icon */}
                      <div className={`text-4xl mb-4 ${
                        index % 2 === 0 ? 'md:text-right' : 'md:text-left'
                      }`}>
                        {step.icon || '⭐'}
                      </div>

                      <h3 className="text-xl md:text-2xl font-poppins font-bold text-lion-blue mb-3 group-hover:text-lion-yellow transition-colors duration-300">
                        {step.title}
                      </h3>
                      
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>

                      {/* Arrow for visual flow */}
                      {index < displaySteps.length - 1 && (
                        <div className={`hidden md:flex mt-6 ${
                          index % 2 === 0 ? 'justify-end' : 'justify-start'
                        }`}>
                          <ArrowRight className="w-6 h-6 text-lion-yellow opacity-60" />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {error && (
          <motion.p 
            variants={itemVariants}
            className="text-gray-500 text-center mt-8"
          >
            Process information will be loaded shortly. Please check back soon.
          </motion.p>
        )}

        {/* Call to Action */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16 bg-lion-gray p-8 rounded-lg"
        >
          <CheckCircle className="w-12 h-12 text-lion-yellow mx-auto mb-4" />
          <h3 className="text-2xl font-poppins font-bold text-lion-blue mb-4">
            Ready to Start Your Journey?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Let's discuss your project and how our proven process can help you achieve your goals.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center bg-lion-yellow text-lion-blue px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transform hover:scale-105 transition-all duration-300"
          >
            Let's Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;